import * as React from "react";
import * as ReactDOM from "react-dom";
import { lightTheme, darkTheme } from "./themes/theme-context";
import RouterContext from "./routes/routesMapper";
import { UserContext } from "./userProfile/userProfileContext";
import { AppConfigContext } from "./appConfigContext";
import { MuiThemeProvider } from "@material-ui/core/styles";
import "./index.scss";
import "./styles/rmax.icons.scss";
import "./styles/font-family.scss";
import "./styles/print.scss";
import "@babel/polyfill";
// Do not remove this Interceptor import.
import Interceptor from "./auth/interceptor";
import stores from "./stores/index";
import { Provider } from "mobx-react";
import StyledSnackbar from "./StyledSnackbar";
import AuthService from "./auth/AuthService";
import { fetchConfig, getRouteNameEditorParams, getTerminalAccessLabel } from "./utilities/appHelper";
import { ai } from "./services/TelemetryService";
import history from "./utilities/history";
import { getDataWithQueryString } from "./api/componentAPIs";
import { FeatureContext } from "./userProfile/featureFlagsContext";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import appConstants from "./appConstants";

class App extends React.Component {
  constructor(props) {
    super(props);
    this.initializeAppConfiguration();
    
    this.state = {
      theme: lightTheme,
      userData: {
        userProfile: null,
        updateUserProfile: this.userProfileUpdate,
        toggleTheme: this.toggleTheme,
      },
      configJSON: null,
      featureFlagData: {
        featureFlags: [],
        setFeatureFlags: this.setFeatureFlags,
        isFeatureEnabled: this.isFeatureEnabled
      },
    };
  }

   userProfileUpdate = ({geoJson,...profileData}) => {
      this.setState((state) => ({
        userData: {
          ...state.userData,
          userProfile: {
            ...state.userData.userProfile,
            ...profileData,
            geoJson: geoJson ?? state.userData.userProfile.geoJson,
          },
        },
      }));
    };

   setAppConfig = (configJSON) => {
      this.setState({
        configJSON: {
          ...configJSON,
          terminalAccessLabel: getTerminalAccessLabel(
            configJSON.terminalAccessLabelType
          ),
          routeNameEditParams: getRouteNameEditorParams(
            configJSON.routeNameFormatType
          ),
        },
      });
    };


   toggleTheme = (theme) => {
      let themeObj;
      switch (theme) {
        case "light":
          themeObj = lightTheme;
          break;
        case "dark":
          themeObj = darkTheme;
          break;
        default:
          themeObj = lightTheme;
          break;
      }
      if (this.state.theme !== themeObj) {
        this.setState((currentState) => ({
          theme: currentState.theme === darkTheme ? lightTheme : darkTheme,
        }));
        this.userProfileUpdate({
          ...this.state.userData.userProfile,
          theme: themeObj.themeName,
        });
      }
    };

  setFeatureFlags = (featureFlags) => {
    this.setState((state) => ({
      featureFlagData: {
        ...state.featureFlagData,
        featureFlags,
      },
    }));
  };

  isFeatureEnabled = (featureName) => {
    return Boolean(
      this.state.featureFlagData.featureFlags?.find(
        (feature) => feature.id === featureName
      )?.value
    );
  };

  initializeAppConfiguration = () => {
    const fetchConfigPromise = fetchConfig();
    const getConfigFromAPIPromise = getDataWithQueryString(
      "GetConfiguration",
      null
    );
    Promise.all([fetchConfigPromise, getConfigFromAPIPromise]).then(
      ([configJSON, responseData]) => {
        if (configJSON) {
          if (configJSON.applicationInsights.enabled) {
            ai.initialize(
              { history: history },
              configJSON.applicationInsights.InstrumentationKey
            );
          } else {
            datadogRum.init({
              applicationId: configJSON.datadog.applicationId,
              clientToken: configJSON.datadog.clientToken,
              site: configJSON.datadog.site,
              service: configJSON.datadog.service,
              env: configJSON.appEnv.toLowerCase(), //env value always needs to be in lowercase
              version: configJSON.appVersion,
              sampleRate: 100,
              premiumSampleRate: 0,
              trackInteractions: true,
              trackFrustrations: true,
              allowedTracingOrigins: [window.location.origin],
            });
            datadogLogs.init({
              clientToken: configJSON.datadog.clientToken,
              site: configJSON.datadog.site,
              service: configJSON.datadog.service,
              env: configJSON.appEnv.toLowerCase(),
              version: configJSON.appVersion,
              forwardErrorsToLogs: true,
              sampleRate: 100,
            });
          }
        }
        if (responseData && responseData.status === 200) {
          this.setAppConfig({ ...configJSON,...responseData.data });
        }
      },
      (err) => {
        history.push(appConstants.NAV_URLS.SERVER_ERROR);
      }
    );
  };

  render() {
    return (
      <AppConfigContext.Provider value={this.state.configJSON}>
        <UserContext.Provider value={this.state.userData}>
          <FeatureContext.Provider value={this.state.featureFlagData}>
            <MuiThemeProvider theme={this.state.theme}>
              <StyledSnackbar>
                <RouterContext />
              </StyledSnackbar>
            </MuiThemeProvider>
          </FeatureContext.Provider>
        </UserContext.Provider>
      </AppConfigContext.Provider>
    );
  }
}

const renderApp = () =>
  ReactDOM.render(
    <Provider {...stores}>
      <App />
    </Provider>,
    document.getElementById("root")
  );

Interceptor.configure();
AuthService.initKeycloak(renderApp);
